// Resolves the "home" path for the current user based on their permissions
// and role flags. Mirrors the sidebar NAV order in AppShell.jsx so the
// landing page a user sees on sign-in is the same page they'd get by
// clicking the topmost enabled item in the sidebar.
//
// If the user has zero accessible pages, this returns null so the caller
// (App.js `/` route) can render the explicit "No access" panel instead
// of triggering an infinite Navigate → guard → Navigate loop.
//
// Order (top → bottom, matches sidebar):
//   dashboard:read + any widget → "/"
//   horses:read                 → "/horses"
//   rankings:read               → "/horses/rankings"
//   breeding:read               → "/breeding"
//   staff:read                  → "/staff"
//   inventory:read              → "/inventory/feed"
//   invoices:read               → "/invoices"
//   admin                       → "/whatsapp"
//   master                      → "/members"
//   costs:read                  → "/costs"
//
// ── Why we check for widget perms on Dashboard ──
// A user could be granted `dashboard:read` (they can OPEN the page)
// but have every individual widget permission (`dashboard:view_kpi_*`,
// `dashboard:view_daily_summary`, `…`) toggled off. Landing them on
// a functionally empty page is confusing — better to fall through to
// the next accessible sidebar entry so sign-in delivers them to a
// page that actually shows content.

// All per-widget permissions that could make the Dashboard page
// worth landing on. If NONE of these are held (and the user isn't a
// master admin) we treat the Dashboard as empty for routing purposes.
const DASHBOARD_WIDGET_PERMS = [
  "dashboard:view_kpi_horses",
  "dashboard:view_kpi_staff",
  "dashboard:view_kpi_invoices",
  "dashboard:view_kpi_feed_alerts",
  "dashboard:view_daily_summary",
  "dashboard:view_pool_distribution",
  "dashboard:view_top_horses",
  "dashboard:view_feed_alerts",
  "dashboard:view_bot_activity",
  "dashboard:view_audit_activity",
];

export function resolveHomePath({ hasPerm, isAdmin, isMaster } = {}) {
  const perm = typeof hasPerm === "function" ? hasPerm : () => false;

  // Only route to `/` if the Dashboard actually has content to show
  // this user. Master admins bypass the widget check because they
  // implicitly have every permission.
  const dashboardHasContent = isMaster || DASHBOARD_WIDGET_PERMS.some((p) => perm(p));
  if (perm("dashboard:read") && dashboardHasContent) return "/";

  if (perm("horses:read")) return "/horses";
  if (perm("rankings:read")) return "/horses/rankings";
  if (perm("breeding:read")) return "/breeding";
  if (perm("staff:read")) return "/staff";
  if (perm("inventory:read")) return "/inventory/feed";
  if (perm("invoices:read")) return "/invoices";
  if (isAdmin) return "/whatsapp";
  if (isMaster) return "/members";
  if (perm("costs:read")) return "/costs";

  // Fallback: user has `dashboard:read` but no widget perms AND no
  // access to any other page. Still land them on the Dashboard so
  // they see the "cards hidden" empty state (which explains next
  // steps) rather than the raw No-Access panel.
  if (perm("dashboard:read")) return "/";

  return null;
}
